import { createContext, type ReactNode } from 'react';
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser';
import type { AdminSession, AdminUser, LoginResponse } from '@bravemove/types';

/**
 * F06 — AuthContext object + its TypeScript surface. The concrete provider
 * implementation lives in `./AuthProvider.tsx` so this file only exports a
 * non-component constant + types, satisfying `react-refresh/only-export-
 * components`.
 *
 * Access tokens live in component state only (NEVER in localStorage). The
 * refresh cookie (`admin_refresh`, HttpOnly, Path=/admin/auth) keeps the
 * session alive across reloads — on mount the provider posts /admin/auth/refresh
 * once; if it returns a session it hydrates `adminUser` via GET /admin/me.
 */

export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  readonly adminUser: AdminUser | null;
  readonly accessToken: string | null;
  readonly status: AuthStatus;
  readonly login: (email: string, password: string) => Promise<LoginResponse>;
  readonly confirmTotp: (challengeToken: string, code: string) => Promise<AdminSession>;
  readonly confirmSecurityKey: (
    challengeToken: string,
    challengeId: string,
    assertion: AuthenticationResponseJSON,
  ) => Promise<AdminSession>;
  readonly confirmEnrollment: (enrollmentToken: string, code: string) => Promise<AdminSession>;
  readonly logout: () => Promise<void>;
  readonly refresh: () => Promise<void>;
  readonly refreshMe: () => Promise<AdminUser | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  readonly children: ReactNode;
}
